import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import {Injectable} from "@angular/core";
import {Observable} from "rxjs";
import {AuthService} from "./auth/auth-service";
import {environment} from "../../environments/environment";
import { from } from "rxjs";
import { tap } from "rxjs/operators";
import {Store} from "./store.service";
import {VersionService} from "./version/version.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService, public store: Store) {}

  private async loggedIn(req: HttpRequest<any>, next: HttpHandler) {
    const loggedIn = await this.authService.isLoggedIn();
    if (loggedIn === true) {
      const token = await this.authService.getToken();
      const isFormData = req.body instanceof FormData;
      const headers: Record<string, string> = {
        "Content-Language": this.store.state.common.lang,
        Authorization: (token ? "Bearer " + token : ""),
      };
      if (!isFormData) {
        headers["Content-Type"] = "application/json";
      }
      const authReq = req.clone({ setHeaders: headers });
      // Important: Note the .toPromise()
      return next.handle(authReq).pipe(
        tap((event) => this.invalidateDryRunCacheOnMutation(authReq, event)),
      ).toPromise();
    }
    // Important: Note the .toPromise()
    return next.handle(req).pipe(
      tap((event) => this.invalidateDryRunCacheOnMutation(req, event)),
    ).toPromise();
  }

  private invalidateDryRunCacheOnMutation(req: HttpRequest<any>, event: HttpEvent<any>): void {
    if (!(event instanceof HttpResponse)) { return; }
    const method = req.method.toUpperCase();
    if (method === "GET" || method === "HEAD" || method === "OPTIONS") { return; }
    // Skip the dry-run call itself — it's a POST that fetches the data.
    if (req.url.includes("/recover-orphan-config")) { return; }
    // Skip non-API endpoints.
    if (!req.url.startsWith(environment.BASE_URL)) { return; }

    // Always clear the localStorage cache so the next manual debug check is fresh.
    this.store.state.common.dryRunResults.invalidateAll();
    // While debug is open, every save can have changed the error list.
    // dryRunRefresh$ is debounced (500ms) so a burst of autosaves coalesces
    // into a single recover-orphan-config call.
    if (this.store.state.common.debugMode) {
      this.store.triggerDryRunRefresh();
    }
  }

  private static shouldBypass(req: HttpRequest<any>): boolean {
    // Define the pattern for endpoints that should bypass this interceptor
    if (req.url.startsWith(environment.BASE_URL + "translation/cached")) { return true; }
    if (!Boolean(environment.KEYCLOAK_ACTIVE) && req.url.startsWith(environment.CAS_URL)) { return true; }
    if (req.url === VersionService.statusUrl(environment.E_REGISTRATIONS_URL)) { return true; }
    return false;
  }

  public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (AuthInterceptor.shouldBypass(req)) {
      return next.handle(req);
    }
    return from(this.loggedIn(req, next));
  }
}
